import { useMutation } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { useCallback, useState } from 'react';

import { useAuth } from '@/lib/auth/auth-context';
import { resolveFoodRequest } from '@/screens/food-requests/food-requests-query';
import { GymLinkPanel } from '@/screens/gym-catalog/GymLinkPanel';
import { idList, linkGymFoods } from '@/screens/gym-catalog/gym-catalog-query';

import { NewFoodScreen } from './NewFoodScreen';

const route = getRouteApi('/foods/new');

export function NewFoodRoute() {
  const { api } = useAuth();
  const search = route.useSearch();
  const navigate = useNavigate();
  const { requestId } = search;
  const gymFoodIds = idList(search.gymFoodIds);
  /** Made from gyms' foods: the row that exists now, while its gym rows are linked. */
  const [created, setCreated] = useState<{ id: string; name: string } | null>(null);

  /**
   * Answering the request is a second call, not part of the create.
   *
   * Two round-trips can half-fail — the food exists, the request is still open
   * — but that failure is now trivially recoverable, because the request's own
   * resolve picker finds the new row immediately. Folding the request id into
   * `POST /admin/foods` would buy atomicity at the cost of a create endpoint
   * that knows about a screen.
   */
  const resolve = useMutation({
    mutationFn: (foodId: string) => resolveFoodRequest(api, requestId ?? '', { foodId }),
    retry: false,
  });

  /**
   * Made from gyms' foods ("From gyms"): link them, then back to the queue. A
   * failed link stays on this page and says so ([GymLinkPanel]): leaving would
   * show the queue with the group still in it and no word why.
   */
  const link = useMutation({
    mutationFn: (foodId: string) => linkGymFoods(api, gymFoodIds, foodId),
    onSuccess: () => {
      void navigate({ to: '/gym-catalog', replace: true });
    },
    retry: false,
  });

  const onCreated = useCallback(
    (food: { id: string; name: string }) => {
      if (gymFoodIds.length > 0) {
        setCreated(food);
        link.mutate(food.id);
        return;
      }
      if (requestId === undefined) {
        // Straight to the new row's own page: the next thing to do with a
        // food is look at what was actually written.
        void navigate({ to: '/foods/$foodId', params: { foodId: food.id }, replace: true });
        return;
      }

      // To the request, which now shows what its answer wrote and where to revert it.
      resolve.mutate(food.id, {
        onSettled: () => {
          void navigate({ to: '/food-requests/$requestId', params: { requestId }, replace: true });
        },
      });
    },
    [navigate, requestId, resolve, link, gymFoodIds.length],
  );

  const onDiscard = useCallback(() => {
    // Back to wherever this was opened from: the request when it answers one,
    // the catalog otherwise.
    if (gymFoodIds.length === 0 && requestId !== undefined) {
      void navigate({ to: '/food-requests/$requestId', params: { requestId } });
      return;
    }
    void navigate({ to: gymFoodIds.length > 0 ? '/gym-catalog' : '/foods' });
  }, [navigate, requestId, gymFoodIds.length]);

  if (created !== null) {
    return (
      <GymLinkPanel
        noun="food"
        name={created.name}
        count={gymFoodIds.length}
        pending={link.isPending}
        error={link.isError ? link.error.message : null}
        onRetry={() => {
          link.mutate(created.id);
        }}
        onOpen={() => {
          void navigate({ to: '/foods/$foodId', params: { foodId: created.id }, replace: true });
        }}
      />
    );
  }
  return <NewFoodScreen initialName={search.name} onCreated={onCreated} onDiscard={onDiscard} />;
}
